import type { RewardCenterTiersConfigQuery } from "@workspace/casino_core/queries/RewardCenterTiersConfigQuery";
import { CMS_BRAND_SLUG } from "../../constants";
import type {
  CmsTier,
  EnabledTabs,
  FooterLinks,
  TierColors,
  TierConfiguration,
} from "../../types";

const TIER_ID_PREFIX = "LoyaltyPublicTier:";
const LEVEL_ID_PREFIX = "LoyaltyPublicMilestone:";

type Doc = NonNullable<
  NonNullable<RewardCenterTiersConfigQuery["cms"]>["Loyalties"]
>["docs"][number];
type Row = NonNullable<Doc["tiers"]>[number];

type Phrase = Row["name"];

const phraseText = (phrase: Phrase): string | undefined =>
  phrase?.brandOverwrites?.find(
    (overwrite) => overwrite?.brand?.slug === CMS_BRAND_SLUG,
  )?.value ||
  phrase?.value ||
  undefined;

// A missing name must not be a key: the merge in loadTiersConfiguration would
// overwrite the API's tier name with `undefined`.
const withName = (name: string | undefined) => (name ? { name } : {});

const toColors = (row: Row): TierColors | undefined => {
  const { sheen, body, base, bar, badgeLight, badgeDark } = row;
  if (!sheen || !body || !base || !bar || !badgeLight || !badgeDark) return;
  return { sheen, body, base, bar, badgeLight, badgeDark };
};

const toRewards = (rewards: unknown): string[] | undefined =>
  Array.isArray(rewards)
    ? rewards.filter((reward) => typeof reward === "string" && reward !== "")
    : undefined;

const toId = (prefix: string, raw: string) =>
  raw.startsWith(prefix) ? raw : `${prefix}${raw}`;

const toLevels = (
  levels: Row["levels"],
): Record<string, string> | undefined => {
  if (!levels) return;
  const names: Record<string, string> = {};
  for (const level of levels) {
    const levelId = level?.loyaltyLevelId;
    const name = phraseText(level?.name);
    if (!levelId || !name) continue;
    names[toId(LEVEL_ID_PREFIX, String(levelId))] = name;
  }
  return names;
};

// The Rewards tab is always on; History and FAQ only when the CMS checks them.
const toEnabledTabs = (tabs: Doc["enabledTabs"]): EnabledTabs => ({
  history: tabs?.history === true,
  faq: tabs?.faq === true,
});

const toLinks = (links: Doc["links"]): FooterLinks | undefined =>
  links
    ? { sports: links.sports || undefined, casino: links.casino || undefined }
    : undefined;

// `undefined` is a response we cannot read, and the backend retries it. A brand
// without a loyalty doc is a valid, empty configuration: retrying that forever
// would not change the answer.
const mapTierConfiguration = (
  data: RewardCenterTiersConfigQuery | undefined,
): TierConfiguration | undefined => {
  const docs = data?.cms?.Loyalties?.docs;
  if (!docs) return undefined;

  const doc = docs[0];
  const tiers: Record<string, CmsTier> = {};
  for (const row of doc?.tiers ?? []) {
    const tierId = row?.loyaltyTierId;
    const colors = row ? toColors(row) : undefined;
    if (tierId == null || !colors) continue;

    // The CMS stores the full id ("LoyaltyPublicTier:1"); older entries a bare number.
    const id = toId(TIER_ID_PREFIX, String(tierId));
    tiers[id] = {
      id,
      ...colors,
      ...withName(phraseText(row.name)),
      rewards: toRewards(row.rewards),
      levels: toLevels(row.levels),
    };
  }

  return {
    tiers,
    links: toLinks(doc?.links),
    enabledTabs: toEnabledTabs(doc?.enabledTabs),
  };
};

export default mapTierConfiguration;
